import { AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { HelpCircle } from "lucide-react";
import { PollinatorLogo } from "@themes/pollinator/ui/PollinatorLogo";
import { MotionSlideLeft } from "@themes/pollinator/ui/MotionSlideLeft";
import VideoUIControlsContainer from "../VideoUIControls/VideoUIControlsContainer";
import VideoUIGridContainer from "../VideoUIGrid/VideoUIGridContainer";
import VideoUISidebarContainer from "../VideoUISidebar/VideoUISidebarContainer";
import VideoUITileContentShareContainer from "../VideoUITile/VideoUITileContentShareContainer";
import VideoUISubtitles from "../VideoUISubtitles/VideoUISubtitles";
import type { Action } from "@core/features/actions";
import VideoUIIntroDialogContainer from "../VideoUIIntroDialog/VideoUIIntroDialogContainer";
import { MeetingDetails } from "@platform/features/video/types/video.types";
import { SupportDialogs } from "@platform/features/support";
import { Button } from "@themes/pollinator/ui/Button";

type Props = {
  priorityBasedPolicy: boolean;
  sharingAttendeeId: string;
  isSidebarOpen: boolean;
  isSupportDialogEnabled: boolean;
  meetingScriptAction: Action | undefined;
  meetingDetails: MeetingDetails;
  onSupportClick: () => void;
};

export function VideoUIFrame({
  priorityBasedPolicy,
  sharingAttendeeId,
  isSidebarOpen,
  isSupportDialogEnabled = false,
  meetingScriptAction,
  meetingDetails,
  onSupportClick,
}: Props) {
  return (
    <>
      <Helmet>
        <title>
          {["Pollinator", "Meeting", meetingDetails.title]
            .filter(Boolean)
            .join(" | ")}
        </title>
      </Helmet>
      <div className="bg-brand-900 h-[100dvh] w-full flex flex-row overflow-hidden min-w-xs">
        <div className="relative h-full flex flex-1 flex-col justify-between min-w-0">
          <div className="flex justify-center px-4 pt-4 pb-2 md:pb-4 md:pt-6">
            <PollinatorLogo size="sm" theme="light" />
            {isSupportDialogEnabled && (
              <Button
                variant="ghost"
                theme="dark"
                className="absolute right-2 top-2 md:right-4 md:top-6"
                iconComponent={HelpCircle}
                onClick={onSupportClick}
              />
            )}
          </div>
          <div className="relative flex flex-col flex-1 items-center justify-center w-full h-full overflow-hidden px-4 pt-0">
            <VideoUIGridContainer />
            <VideoUITileContentShareContainer />
            <VideoUIIntroDialogContainer meetingDetails={meetingDetails} />
          </div>
          <VideoUISubtitles />
          <VideoUIControlsContainer />
        </div>
        <AnimatePresence mode="wait">
          {isSidebarOpen && (
            <MotionSlideLeft key="video-ui-sidebar" className="h-full">
              <VideoUISidebarContainer
                priorityBasedPolicy={priorityBasedPolicy}
                sharingAttendeeId={sharingAttendeeId}
                meetingScriptAction={meetingScriptAction}
              />
            </MotionSlideLeft>
          )}
        </AnimatePresence>
      </div>
      {isSupportDialogEnabled && <SupportDialogs />}
    </>
  );
}

export default VideoUIFrame;
